import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE_URL = "https://shattaraai.com";
const DEFAULT_TITLE = "Shattara AI — The AI Tutor Built for Students";
const DEFAULT_DESCRIPTION =
  "Upload your materials. Get instant explanations, quizzes & flashcards personalized to your exact syllabus. Study smarter — not longer.";
const DEFAULT_OG_IMAGE = "https://shattaraai.com/og-image.png";

interface PageMetaOptions {
  noIndex?: boolean;
  ogImage?: string;
  ogType?: string;
}

function setMetaTag(attr: string, key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkTag(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export function usePageMeta(
  title: string,
  description: string,
  options: PageMetaOptions = {}
) {
  const location = useLocation();

  useEffect(() => {
    const { noIndex = false, ogImage = DEFAULT_OG_IMAGE, ogType = "website" } = options;

    // Title
    document.title = title;

    // Meta description
    setMetaTag("name", "description", description);

    // Canonical — strip trailing slash and query params
    const pathname = location.pathname.replace(/\/+$/, "") || "/";
    const canonical = `${SITE_URL}${pathname}`;
    setLinkTag("canonical", canonical);

    // Robots
    if (noIndex) {
      setMetaTag("name", "robots", "noindex, nofollow");
    } else {
      setMetaTag("name", "robots", "index, follow");
    }

    // Open Graph
    setMetaTag("property", "og:title", title);
    setMetaTag("property", "og:description", description);
    setMetaTag("property", "og:url", canonical);
    setMetaTag("property", "og:image", ogImage);
    setMetaTag("property", "og:type", ogType);

    // Twitter
    setMetaTag("name", "twitter:title", title);
    setMetaTag("name", "twitter:description", description);
    setMetaTag("name", "twitter:image", ogImage);
    setMetaTag("name", "twitter:card", "summary_large_image");

    // Cleanup — restore defaults on unmount
    return () => {
      document.title = DEFAULT_TITLE;
      setMetaTag("name", "description", DEFAULT_DESCRIPTION);
      setMetaTag("name", "robots", "index, follow");
      setMetaTag("property", "og:title", DEFAULT_TITLE);
      setMetaTag("property", "og:description", DEFAULT_DESCRIPTION);
      setMetaTag("property", "og:url", SITE_URL);
      setMetaTag("property", "og:image", DEFAULT_OG_IMAGE);
      setMetaTag("property", "og:type", "website");
      setMetaTag("name", "twitter:title", DEFAULT_TITLE);
      setMetaTag("name", "twitter:description", DEFAULT_DESCRIPTION);
      setMetaTag("name", "twitter:image", DEFAULT_OG_IMAGE);
    };
  }, [title, description, location.pathname, options.noIndex, options.ogImage, options.ogType]);
}
